import React from 'react';
import { AntdConfig } from '@react-awesome-query-builder/antd';
import {
  Utils,
  Query,
  Builder,
  BasicConfig,
  BasicFuncs
} from '@react-awesome-query-builder/ui';
import ru_RU from 'antd/es/locale/ru_RU';
import CustomMultiSelect from './QueryBuilder/CustomMultiSelect';

const InitialConfig = AntdConfig;

export const numberOperators = [
  'equal',
  'not_equal',
  'less',
  'less_or_equal',
  'greater',
  'greater_or_equal'
];

const conjunctions = {
  ...InitialConfig.conjunctions
};
const proximity = {
  ...InitialConfig.operators.proximity,
  valueLabels: [
    { label: 'Word 1', placeholder: 'Enter first word' },
    { label: 'Word 2', placeholder: 'Enter second word' }
  ],
  textSeparators: [
    //'Word 1',
    //'Word 2'
  ],
  options: {
    ...InitialConfig.operators.proximity.options,
    optionLabel: 'Near', // label on top of "near" selectbox (for config.settings.showLabels==true)
    optionTextBefore: 'Near', // label before "near" selectbox (for config.settings.showLabels==false)
    optionPlaceholder: 'Select words between', // placeholder for "near" selectbox
    minProximity: 2,
    maxProximity: 10,
    defaults: {
      proximity: 2
    },
    customProps: {}
  }
};

const operators = {
  ...InitialConfig.operators,
  // examples of  overriding
  proximity,
  between: {
    ...InitialConfig.operators.between,
    valueLabels: ['Value from', 'Value to'],
    textSeparators: ['from', 'to']
  },
  is_in: {
    isNotOp: false,
    label: 'is_in',
    reversedOp: 'sl_not_in',
    labelForFormat: 'IN',
    sqlOp: 'IN',
    valueSources: ['value'],
    mongoFormatOp: '',
    jsonLogic: 'in',
    formatOp: (
      field,
      op,
      values,
      valueSrc,
      valueType,
      opDef,
      operatorOptions,
      isForDisplay
    ) => {
      return `${field} IN (${values
        .replace(/["']/g, '')
        .split(',')
        .map((word) => `${word.trim()}`)
        .join(',')})`;
    },
    sqlFormatOp: (
      field,
      op,
      values,
      valueSrc,
      valueType,
      opDef,
      operatorOptions,
      fieldDef
    ) => {
      if (valueSrc == 'value') {
        return `${field} IN (${values
          .replace(/["']/g, '')
          .split(',')
          .map((word) => `${word.trim()}`)
          .join(',')})`;
      }
    }
  },
  is_in_lookup_list: {
    isNotOp: false,
    label: 'is_in_lookup_list',
    reversedOp: 'sl_not_in',
    labelForFormat: 'IN',
    sqlOp: 'IN',
    valueSources: ['value'],
    mongoFormatOp: '',
    jsonLogic: 'in',
    formatOp: (
      field,
      op,
      values,
      valueSrc,
      valueType,
      opDef,
      operatorOptions,
      isForDisplay
    ) => {
      return `${field} IN (${values
        .replace(/["']/g, '')
        .split(',')
        .map((word) => `${word.trim()}`)
        .join(',')})`;
    },
    sqlFormatOp: (
      field,
      op,
      values,
      valueSrc,
      valueType,
      opDef,
      operatorOptions,
      fieldDef
    ) => {
      if (valueSrc == 'value') {
        return `${field} IN (${values
          .replace(/["']/g, '')
          .split(',')
          .map((word) => `${word.trim()}`)
          .join(',')})`;
      }
    }
  }
};

const widgetsData = (matchList = [], allFunc = []) => ({
  ...InitialConfig.widgets,
  // examples of overriding
  text: {
    ...InitialConfig.widgets.text
  },
  textarea: {
    ...InitialConfig.widgets.textarea,
    maxRows: 3
  },
  slider: {
    ...InitialConfig.widgets.slider
  },
  rangeslider: {
    ...InitialConfig.widgets.rangeslider
  },
  date: {
    ...InitialConfig.widgets.date,
    dateFormat: 'DD.MM.YYYY',
    valueFormat: 'YYYY-MM-DD'
  },
  time: {
    ...InitialConfig.widgets.time,
    timeFormat: 'HH:mm',
    valueFormat: 'HH:mm:ss'
  },
  datetime: {
    ...InitialConfig.widgets.datetime,
    timeFormat: 'HH:mm',
    dateFormat: 'DD.MM.YYYY',
    valueFormat: 'YYYY-MM-DDTHH:mm:ss[Z]'
  },
  func: {
    ...InitialConfig.widgets.func,
    customProps: {
      showSearch: true
    }
  },
  select: {
    ...InitialConfig.widgets.select
  },
  multiselect: {
    ...InitialConfig.widgets.multiselect,
    customProps: {
      //showCheckboxes: false,
      width: '200px',
      input: {
        width: '100px'
      }
    }
  },
  treeselect: {
    ...InitialConfig.widgets.treeselect,
    customProps: {
      showSearch: true
    }
  },
  list_text: {
    type: 'list_text',
    jsType: 'array',
    valueSrc: 'value',
    valueLabel: 'Values',
    valuePlaceholder: 'Select values',
    formatValue: function (vals, fieldDef, wgtDef, isForDisplay) {
      const treeData = fieldDef.customListValues;
      let valsLabels = vals.map((v) =>
        this.utils.getTitleInListValues(treeData, v)
      );
      return isForDisplay
        ? valsLabels.map(this.utils.stringifyForDisplay)
        : vals.map(JSON.stringify);
    },
    sqlFormatValue: function (vals, fieldDef, wgtDef, op, opDef) {
      return vals.map((v) => this.utils.SqlString.escape(v));
    },
    spelFormatValue: function (val) {
      return this.utils.spelEscape(val);
    },
    toJS: (val) => val,
    factory: (props) => {
      let fieldList = allFunc
        .filter((a) => a.args.aggregationFunction === 'collect_set')
        .map((m) => ({
          title: m.name,
          value: m.name
        }));
      return (
        <CustomMultiSelect
          customListValues={
            props.operator === 'is_in_lookup_list' ? matchList : fieldList
          }
          {...props}
          allowCustomValues={props.operator === 'is_in'}
          multiple={true}
        />
      );
    }
  }
});

export const textOperators = [
  'equal',
  'not_equal',
  'like',
  'starts_with',
  'ends_with',
  'not_like',
  'is_in',
  'is_in_lookup_list'
];

const types = {
  ...InitialConfig.types,
  text: {
    ...InitialConfig.types.text,
    excludeOperators: ['proximity'],
    widgets: {
      ...InitialConfig.types.text.widgets,
      // multiselect: {
      //   operators: ['is_in'],
      // },
      list_text: {
        operators: ['is_in', 'is_in_lookup_list']
      }
    }
  },
  boolean: {
    ...InitialConfig.types.boolean,
    widgets: {
      ...InitialConfig.types.boolean.widgets,
      boolean: {
        ...InitialConfig.types.boolean.widgets.boolean,
        opProps: {
          equal: {
            label: 'is'
          },
          not_equal: {
            label: 'is not'
          }
        }
      }
    }
  },
  list_text: {
    defaultOperator: 'is_in_lookup_list',
    widgets: {
      list_text: {
        operators: ['is_in_lookup_list']
      }
    }
  }
};

const localeSettings = {
  locale: ru_RU,
  valueLabel: 'Value',
  valuePlaceholder: 'Value',
  fieldLabel: 'Field',
  operatorLabel: 'Operator',
  funcLabel: 'Function',
  fieldPlaceholder: 'Select field',
  funcPlaceholder: 'Select function',
  operatorPlaceholder: 'Select operator',
  lockLabel: 'Lock',
  lockedLabel: 'Locked',
  deleteLabel: undefined,
  addGroupLabel: 'Add group',
  addRuleLabel: 'Add rule',
  addSubRuleLabel: 'Add sub rule',
  delGroupLabel: undefined,
  fieldSourcesPopupTitle: 'Select source',
  valueSourcesPopupTitle: 'Select value source',
  removeRuleConfirmOptions: {
    title: 'Are you sure delete this rule?',
    okText: 'Yes',
    okType: 'danger',
    cancelText: 'Cancel'
  },
  removeGroupConfirmOptions: {
    title: 'Are you sure delete this group?',
    okText: 'Yes',
    okType: 'danger',
    cancelText: 'Cancel'
  }
};

const settings = {
  ...InitialConfig.settings,
  ...localeSettings,

  defaultSliderWidth: '200px',
  defaultSelectWidth: '200px',
  defaultSearchWidth: '100px',
  defaultMaxRows: 5,

  valueSourcesInfo: {
    value: {
      label: 'Value'
    },
    field: {
      label: 'Field',
      widget: 'field'
    },
    func: {
      label: 'Function',
      widget: 'func'
    }
  },
  fieldSources: ['field', 'func'],
  keepInputOnChangeFieldSrc: true,
  maxNesting: 5,
  canLeaveEmptyGroup: true,
  shouldCreateEmptyGroup: true,
  showErrorMessage: true,
  customFieldSelectProps: {
    showSearch: true
  }
};

const funcs = {
  //...BasicFuncs
  string: {
    type: '!struct',
    label: 'String',
    subfields: {
      LOWER: {
        ...BasicFuncs.LOWER,
        operators: textOperators,
        args: {
          str: {
            label: 'String',
            type: 'text',
            valueSources: ['value', 'field']
          }
        },
        fieldSettings: {
          listValues: [],
          allowCustomValues: true
        }
      },
      UPPER: {
        ...BasicFuncs.UPPER,
        operators: textOperators,
        args: {
          str: {
            label: 'String',
            type: 'text',
            valueSources: ['value', 'field']
          }
        },
        fieldSettings: {
          listValues: [],
          allowCustomValues: true
        }
      }
    }
  },
  date: {
    type: '!struct',
    label: 'Date',
    subfields: {
      RELATIVE_DATETIME: {
        ...BasicFuncs.RELATIVE_DATETIME,
        operators: numberOperators,
        args: {
          ...BasicFuncs.RELATIVE_DATETIME.args,
          date: {
            ...BasicFuncs.RELATIVE_DATETIME.args.date,
            defaultValue: { args: [] }
          }
        }
      }
    }
  },
  number: {
    type: '!struct',
    label: 'Number',
    subfields: {
      LINEAR_REGRESSION: {
        ...BasicFuncs.LINEAR_REGRESSION,
        operators: numberOperators,
        args: {
          ...BasicFuncs.LINEAR_REGRESSION.args,
          val: {
            label: 'Value',
            type: 'number',
            valueSources: ['field']
          }
        }
      }
    }
  }
};

const ctx = InitialConfig.ctx;

export const booleanOperators = ['equal', 'not_equal'];

export const tagsData = [
  {
    label: 'Sender',
    value: 'sender_hashcode'
  },
  {
    label: 'Receiver',
    value: 'receiver_hashcode'
  },
  {
    label: 'SendingPartner',
    value: 'sending_partner_code'
  },
  {
    label: 'ReceivingPartner',
    value: 'receiving_partner_code'
  },
  {
    label: 'ReceiverPayer',
    value: 'receiver_payer_name'
  }
];

export const fields = {
  additional_info: {
    label: 'additional_info',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  aggregated_transaction: {
    label: 'aggregated_transaction',
    type: 'boolean',
    defaultValue: true,
    operators: booleanOperators,
    mainWidgetProps: {
      labelYes: '+',
      labelNo: '-'
    }
  },
  credit_party: {
    label: 'credit_party',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  document_reference_number: {
    label: 'document_reference_number',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  external_code: {
    label: 'external_code',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  external_id: {
    label: 'external_id',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  intermediary: {
    label: 'intermediary',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  purpose_code: {
    label: 'purpose_code',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  receiver_address: {
    label: 'receiver_address',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  receiver_advance_hashcode: {
    label: 'receiver_advance_hashcode',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  receiver_amount: {
    label: 'receiver_amount',
    type: 'number',
    operators: numberOperators,
    fieldSettings: {
      min: 0
    }
  },
  receiver_birth_country: {
    label: 'receiver_birth_country',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  receiver_city: {
    label: 'receiver_city',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  receiver_country: {
    label: 'receiver_country',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  receiver_country_risk_level: {
    label: 'receiver_country_risk_level',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  receiver_currency: {
    label: 'receiver_currency',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  receiver_first_name: {
    label: 'receiver_first_name',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  receiver_gender: {
    label: 'receiver_gender',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  receiver_hashcode: {
    label: 'receiver_hashcode',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  receiver_id_number: {
    label: 'receiver_id_number',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  receiver_id_type: {
    label: 'receiver_id_type',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  receiver_identifier_hashcode: {
    label: 'receiver_identifier_hashcode',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  receiver_incorporation_date: {
    label: 'receiver_incorporation_date',
    type: 'datetime',
    operators: numberOperators,
    valueSources: ['value', 'func']
  },
  receiver_last_name: {
    label: 'receiver_last_name',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  receiver_msisdn: {
    label: 'receiver_msisdn',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  receiver_nationality: {
    label: 'receiver_nationality',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  receiver_occupation: {
    label: 'receiver_occupation',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  receiver_payer_country: {
    label: 'receiver_payer_country',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  receiver_payer_name: {
    label: 'receiver_payer_name',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  receiver_payer_service: {
    label: 'receiver_payer_service',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  receiver_postal_code: {
    label: 'receiver_postal_code',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  receiver_state: {
    label: 'receiver_state',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  receiver_type: {
    label: 'receiver_type',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  receiving_partner: {
    label: 'receiving_partner',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  receiving_partner_code: {
    label: 'receiving_partner_code',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  receiving_partner_risk_level: {
    label: 'receiving_partner_risk_level',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  receiving_partner_type: {
    label: 'receiving_partner_type',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  rfi_sent: {
    label: 'rfi_sent',
    type: 'boolean',
    defaultValue: true,
    operators: booleanOperators,
    mainWidgetProps: {
      labelYes: '+',
      labelNo: '-'
    }
  },
  sender_address: {
    label: 'sender_address',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  sender_advance_hashcode: {
    label: 'sender_advance_hashcode',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  sender_amount: {
    label: 'sender_amount',
    type: 'number',
    fieldSettings: {
      min: 0
    },
    operators: numberOperators
  },
  sender_amount_usd: {
    label: 'sender_amount_usd',
    type: 'number',
    fieldSettings: {
      min: 0
    },
    operators: numberOperators
  },
  sender_birth_country: {
    label: 'sender_birth_country',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  sender_city: {
    label: 'sender_city',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  sender_country: {
    label: 'sender_country',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  sender_country_risk_level: {
    label: 'sender_country_risk_level',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  sender_currency: {
    label: 'sender_currency',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  sender_first_name: {
    label: 'sender_first_name',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  sender_gender: {
    label: 'sender_gender',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  sender_hashcode: {
    label: 'sender_hashcode',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  sender_id_number: {
    label: 'sender_id_number',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  sender_id_type: {
    label: 'sender_id_type',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  sender_identifier_hashcode: {
    label: 'sender_identifier_hashcode',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  sender_incorporation_date: {
    label: 'sender_incorporation_date',
    type: 'datetime',
    operators: numberOperators,
    valueSources: ['value', 'func']
  },
  sender_last_name: {
    label: 'sender_last_name',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  sender_msisdn: {
    label: 'sender_msisdn',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  sender_nationality: {
    label: 'sender_nationality',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  sender_occupation: {
    label: 'sender_occupation',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  sender_postal_code: {
    label: 'sender_postal_code',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  sender_receiver_relationship: {
    label: 'sender_receiver_relationship',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  sender_state: {
    label: 'sender_state',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  sender_type: {
    label: 'sender_type',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  sending_partner: {
    label: 'sending_partner',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  sending_partner_code: {
    label: 'sending_partner_code',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  sending_partner_risk_level: {
    label: 'sending_partner_risk_level',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  sending_partner_type: {
    label: 'sending_partner_type',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  source_of_funds: {
    label: 'source_of_funds',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  transaction_id: {
    label: 'transaction_id',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  txn_date_time: {
    label: 'txn_date_time',
    type: 'datetime',
    operators: numberOperators,
    valueSources: ['value', 'func']
  },
  txn_geography_risk: {
    label: 'txn_geography_risk',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  txn_status: {
    label: 'txn_status',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  txn_type: {
    label: 'txn_type',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  },
  txn_type_code: {
    label: 'txn_type_code',
    type: 'text',
    operators: textOperators,
    fieldSettings: {
      listValues: [],
      allowCustomValues: true
    }
  }
};

export const group_by = [
  {
    value: 'sender_hashcode',
    label: 'sender_hashcode'
  },
  {
    value: 'txn_type',
    label: 'txn_type'
  },
  {
    value: 'sender_msisdn',
    label: 'sender_msisdn'
  },
  {
    value: 'receiver_msisdn',
    label: 'receiver_msisdn'
  },
  {
    value: 'credit_party',
    label: 'credit_party'
  },
  {
    value: 'grouping_key_invariant',
    label: 'grouping_key_invariant'
  },
  {
    value: 'receiver_hashcode',
    label: 'receiver_hashcode'
  },
  {
    value: 'receiving_partner',
    label: 'receiving_partner'
  },
  {
    value: 'sending_partner',
    label: 'sending_partner'
  },
  {
    value: 'sender_advance_hashcode',
    label: 'sender_advance_hashcode'
  },
  {
    value: 'receiver_advance_hashcode',
    label: 'receiver_advance_hashcode'
  }
];

// config = addMixins(config, [
//   "rangeslider",
//   "treeselect",
//   "treemultiselect",
// ]);

export const config = (matchList, allFunc = []) => ({
  ctx,
  conjunctions,
  operators,
  widgets: widgetsData(matchList, allFunc),
  types,
  settings,
  fields,
  funcs
});
